import _ from 'underscore';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import DayPicker, { LocaleUtils } from 'react-day-picker';
import {
	isBefore,
	format,
	parseISO,
	startOfDay,
	isEqual,
	isSameDay,
	endOfDay
} from 'date-fns';
import ruLocale from 'date-fns/locale/ru';
import { getDateRangeText, weekdaysShort } from '@culture/common-helpers/date';

import Icon from 'components/Icon';
import { prepareDates } from 'utils/helpers/dates';
import TopBar from './TopBar';

import styles from './styles.module.scss';

const currentYear = new Date().getFullYear();
const toMonth = new Date(currentYear + 10, 11);

const DatePicker = ({
	onDayApply,
	availableStartDate,
	availableDates,
	startDate,
	endDate,
	dates,
	setDates,
	titleStatus
}) => {
	const [month, setMonth] = useState(new Date());

	const [preparedAvailableDates, setPreparedAvailableDates] = useState(
		prepareDates(availableDates)
	);

	useEffect(() => {
		setPreparedAvailableDates(prepareDates(availableDates));
	}, [availableDates]);

	useEffect(() => {
		setMonth(
			startDate ||
				(!_.isEmpty(preparedAvailableDates)
					? parseISO(preparedAvailableDates[0])
					: availableStartDate)
		);
	}, [preparedAvailableDates, availableStartDate, startDate]);

	const modifiers = useMemo(
		() => ({
			start: dates.startDate || (_.isEmpty(dates) && startDate),
			end: dates.endDate || (_.isEmpty(dates) && endDate),
			disabled: (day) => {
				if (preparedAvailableDates) {
					return !preparedAvailableDates.some((currentDay) =>
						isSameDay(parseISO(currentDay), day)
					);
				}

				return isBefore(startOfDay(day), startOfDay(availableStartDate));
			}
		}),
		[dates, startDate, endDate, preparedAvailableDates, availableStartDate]
	);

	const onDateChange = (date, { disabled }) => {
		if (!disabled) {
			setMonth(date);

			const startOfDayDate = startOfDay(date);
			let newEndDate;

			if (modifiers.start) {
				if (modifiers.end || isBefore(date, modifiers.start)) {
					setDates({
						startDate: startOfDayDate,
						endDate: null
					});
				} else if (!isEqual(startOfDayDate, startOfDay(modifiers.start))) {
					newEndDate = endOfDay(date);
				}
			} else {
				setDates((prevDates) => ({
					...prevDates,
					startDate: startOfDayDate
				}));
			}

			if (newEndDate) {
				setDates((prevDates) => ({ ...prevDates, endDate: newEndDate }));
			}
		}
	};

	const onDateApply = () => {
		onDayApply(dates);
	};

	const onDateClear = () => {
		setDates({
			startDate: undefined,
			endDate: undefined
		});
		onDayApply({});
	};

	const selectedDays = modifiers.start && [
		modifiers.start,
		{ from: modifiers.start, to: modifiers.end }
	];

	const renderDay = (day) => (
		<div className="DayPicker-DayText">{day.getDate()}</div>
	);

	const dateRangeText = getDateRangeText(dates, 'd MMMM');

	const dayPickerCx = cx({
		'DayPicker--single-day': !modifiers.end,
		'DayPicker--two-days': modifiers.end
	});

	return (
		<div className={styles.wrap}>
			<div className={styles.body}>
				<DayPicker
					className={dayPickerCx}
					fromMonth={availableStartDate}
					toMonth={toMonth}
					month={month}
					navbarElement={({ ...rest }) => (
						<TopBar
							{...rest}
							setDates={setDates}
							setMonth={setMonth}
							titleStatus={titleStatus}
							availableDates={preparedAvailableDates}
							modifiers={modifiers}
						/>
					)}
					captionElement={({ date }) => (
						<div className="DayPicker-Caption-Inner">
							{/* eslint-disable-next-line react/button-has-type,jsx-a11y/control-has-associated-label */}
							<button
								className="DayPicker-Caption-LeftBtn"
								onClick={() => {
									setMonth((prev) => {
										const newMonth = new Date(prev);
										newMonth.setMonth(prev.getMonth() - 1);
										return newMonth;
									});
								}}
							>
								<Icon icon="arrow-down" className={styles.arrowIcon} />
							</button>
							<div className="DayPicker-Caption-Title">
								{format(date, 'LLLL yyyy', { locale: ruLocale })}
							</div>
							{/* eslint-disable-next-line react/button-has-type,jsx-a11y/control-has-associated-label */}
							<button
								onClick={() => {
									setMonth((prev) => {
										const newMonth = new Date(prev);
										newMonth.setMonth(prev.getMonth() + 1);
										return newMonth;
									});
								}}
								className="DayPicker-Caption-RightBtn"
							>
								<Icon icon="arrow-down" className={styles.arrowIcon} />
							</button>
						</div>
					)}
					renderDay={renderDay}
					modifiers={modifiers}
					onDayClick={onDateChange}
					selectedDays={selectedDays}
					localeUtils={LocaleUtils}
					locale="ru"
					weekdaysShort={weekdaysShort}
					fixedWeeks
					firstDayOfWeek={1}
				/>
			</div>
			{dates.startDate && (
				<div className={styles.footer}>
					{dates.startDate && (
						<button
							className={styles.clearBtn}
							type="button"
							onClick={onDateClear}
						>
							Очистить
						</button>
					)}
					<button
						className={styles.applyBtn}
						type="button"
						onClick={onDateApply}
					>
						{!dates.startDate || (!dates.endDate && 'Выбрать')} {dateRangeText}
					</button>
				</div>
			)}
		</div>
	);
};

DatePicker.propTypes = {
	startDate: PropTypes.object,
	endDate: PropTypes.object,
	onDayApply: PropTypes.func,
	availableStartDate: PropTypes.object,
	availableDates: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.object])
	),
	dates: PropTypes.object,
	titleStatus: PropTypes.bool,
	setDates: PropTypes.func
};

DatePicker.defaultProps = {
	onDayApply: _.noop,
	setDates: _.noop,
	dates: {},
	titleStatus: true,
	availableStartDate: new Date()
};

export default DatePicker;
